import { useSelector, useDispatch } from "react-redux";
import {
  actions,
  mutations,
  selectors,
} from "../../../../../../../redux/features/account/accountSlice";
import { actions as loaderActions } from "../../../../../../../redux/features/loader/loaderSlice";
import {
  getPanelsByTabId,
  createNewPanelInTabDashboard,
  getXyChartConfigurationByPanelId,
  getConsumeDataByPanelId,
  getPieConfigurationByPanelId,
  getTableConfigurationByPanelId,
  getKpiConfigurationByPanelId,
  updatePanelDashboardPosition,
} from "../../../../../services/DashboardConfigurationService";
import { fetchPanelSizeRestrictions } from "../../../../../services/PanelSizeRestrictionsService";
import axios from "axios";
import { environment } from "../../../../../../../environments/environment";
import { useGridStackContext } from "../../../../../../../contexts/grid-stack-context";
import { useEffect, useState, useRef, useCallback } from "react";

export const usePredefinedPanelConfiguration = (
  actionsFromModule,
  onClose,
  isOpen
) => {
  const { events, panels: currentPanels } = useGridStackContext();

  const dispatch = useDispatch();
  const categories = useSelector(selectors.selectAccountCategories);
  const subcategories = useSelector(selectors.selectAccountSubcategories);
  const predefinedPanels = useSelector(selectors.selectAccountPanels);
  const selectedPrefinedPanels = useSelector(
    selectors.selectAccountSelectedPanels
  );
  const panelLoadingMap = useSelector(selectors.selectPanelLoadingMap);
  const workStream = useSelector(selectors.selectWorkStream);
  const activeTabId = useSelector(selectors.selectTabId);
  const [normalizedPanels, setNormalizedPanels] = useState([]);
  const [panelLoadingState, setPanelLoadingState] = useState({});
  const panelCacheRef = useRef({});

  // Control de fetch en curso por panel
  const panelFetchStatusRef = useRef({});

  const onCategoryClick = async (isSelected, categoryId) => {
    dispatch(mutations.toggleCategory({ categoryId, isSelected }));
  };

  const onSubcategoryClick = async (isSelected, subcategoryId) => {
    dispatch(mutations.toggleSubcategory({ subcategoryId, isSelected }));
  };

  const onPanelClick = (panel) => {
    dispatch(actions.toggleSelectedPanel({ selectedPanel: panel }));
  };

  // Limpiar la selección de paneles cada vez que se abra el diálogo
  useEffect(() => {
    if (isOpen) {
      dispatch(actions.setSelectedPanels([]));
    }
  }, [isOpen, dispatch]);

const importPanelsToGridstack = async () => {
  try {
    dispatch(loaderActions.showLoader());
    onClose?.();

    const currentPanelsList = Object.values(currentPanels) || [];
    console.log("Current panels from context:", currentPanelsList);

    const newPanels = [];

    // 1) Crear e importar cada panel individualmente (SIN x/y)
    for (const selectedPanel of selectedPrefinedPanels) {
      console.log("Creating panel:", selectedPanel);

      // Evitar duplicados por id / tableId
      const exists = currentPanelsList.find((p) => {
        const panelId = selectedPanel.id;
        if (p.tableId && panelId && p.tableId.toString() === panelId.toString()) return true;
        if (p.id && panelId && p.id.toString() === panelId.toString()) return true;
        return false;
      });

      if (exists) {
        console.warn(
          `Panel with ID ${selectedPanel.id} already exists. Skipping import to avoid duplicates.`,
          exists
        );
        continue;
      }

      // Crea el panel SIN x/y (ni yPos): deja que GridStack lo auto-coloque
      const newPanel = await createOptimizedPanelInTab(
        {
          panelType: selectedPanel.panelType,
          panelId: selectedPanel.id,
          configuration: selectedPanel.configuration,
          title: selectedPanel.title,
          description: selectedPanel.description,
          height: selectedPanel.initialHeight || selectedPanel.height || 20, // h por defecto
          width: selectedPanel.initialWidth || selectedPanel.width || 50,   // w por defecto
          // NO enviar yPos/x/y aquí
        },
        activeTabId
      );

      // Marca para auto-colocación y asegura w/h
      newPanel.autoPosition = true;
      newPanel.x = undefined;
      newPanel.y = undefined;
      newPanel.w = newPanel.w != null ? newPanel.w
                : (newPanel.width != null ? newPanel.width
                : (selectedPanel.initialWidth != null ? selectedPanel.initialWidth
                : (selectedPanel.width != null ? selectedPanel.width : 50)));

      newPanel.h = newPanel.h != null ? newPanel.h
                : (newPanel.height != null ? newPanel.height
                : (selectedPanel.initialHeight != null ? selectedPanel.initialHeight
                : (selectedPanel.height != null ? selectedPanel.height : 20)));

      newPanels.push(newPanel);
      console.log("Panel created:", newPanel);
    }

    // 2) Agregar al grid sin afectar los existentes (batch) y compactar
    if (newPanels.length > 0) {
      console.log("Adding only new panels without affecting existing ones");

      const gridContainer = document.getElementById("grid-stack-container-ref");
      const grid = (gridContainer && gridContainer.gridstack) || (typeof window !== "undefined" ? window.grid : null);

      if (grid && typeof grid.batchUpdate === "function") grid.batchUpdate();
      for (const np of newPanels) {
        if (events.onAddExistingPanel) {
          console.log("Adding only new panel to GridStack:", np);
          // Asegúrate que onAddExistingPanel haga grid.addWidget({ x, y, w, h, autoPosition: true, ... })
          events.onAddExistingPanel({
            ...np,
            autoPosition: true,
            x: undefined,
            y: undefined,
            w: np.w,
            h: np.h,
          });
        }
      }
      if (grid && typeof grid.commit === "function") grid.commit();
      if (grid && typeof grid.compact === "function") {
        try { grid.compact(); } catch (e) { console.warn("grid.compact() not available or failed:", e); }
      }

      // 3) Actualiza Redux con todos los paneles (estado local)
      const allPanels = [...currentPanelsList, ...newPanels];
      actionsFromModule?.upgradeToGridstack(allPanels);

      // 4) DESPUÉS de que GridStack haya colocado todo en el DOM, lee posiciones reales y envíalas al backend
      //    usamos un pequeño delay para asegurar render
      setTimeout(async () => {
        try {
          const payload = buildPositionsPayloadForBackend({
            containerId: "grid-stack-container-ref",
            panelsToPersist: newPanels,           // si quieres persistir SOLO los nuevos
            // panelsToPersist: allPanels,        // o TODOS (descomenta esta línea y comenta la de arriba)
            activeTabId,
            accountId: String(localStorage.getItem("u_account_id") ?? "1"),  // ajusta según tu estado/contexto
          });

          console.log("Sending positions payload to backend:", payload.length, payload);

          await sendPositionsToBackend(payload); // POST a tu servicio
          console.log("Positions successfully persisted to backend");
        } catch (persistErr) {
          console.error("Error persisting positions:", persistErr);
        }
      }, 150); // 150-300ms suele bastar; ajusta si lo necesitas

      // 5) Scroll automático al último panel importado (igual que tenías)
      setTimeout(() => {
        try {
          console.log("Iniciando scroll automático hacia los paneles importados");
          const lastImportedPanel = newPanels[newPanels.length - 1];
          if (!lastImportedPanel) return;

          const container = document.getElementById("grid-stack-container-ref");
          if (!container) return;

          const lastWidget = container.querySelector(`[gs-id="${lastImportedPanel.id}"]`);
          if (lastWidget) {
            lastWidget.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
          } else {
            // Fallback aproximado si no encuentras el widget
            const y = lastImportedPanel.y != null ? lastImportedPanel.y : 0;
            const h = lastImportedPanel.h != null ? lastImportedPanel.h : 20;
            const scrollTarget = (y + h) * 20;
            const scrollableContainer =
              container.closest(".dashboard-content-wrapper") ||
              container.closest(".MuiBox-root") ||
              container.parentElement ||
              document.documentElement;

            if (scrollableContainer === document.documentElement) {
              window.scrollTo({ top: scrollTarget, behavior: "smooth" });
            } else {
              scrollableContainer.scrollTo({ top: scrollTarget, behavior: "smooth" });
            }
          }
        } catch (scrollError) {
          console.error("Error durante el scroll automático:", scrollError);
        }
      }, 600);
    }

    dispatch(loaderActions.hideLoader());
  } catch (error) {
    console.error("Error creating panels:", error);
    dispatch(loaderActions.hideLoader());
  }
};

/**
 * Lee del DOM las posiciones reales (gs-x, gs-y, gs-w, gs-h) de los paneles indicados
 * y produce el payload que espera tu servicio `update-dashboard-panel-position`.
 *
 * @param {{containerId: string, panelsToPersist: any[], activeTabId: number|string, accountId: string}} args
 * @returns {Array<Object>}
 */
function buildPositionsPayloadForBackend(args) {
  const { containerId, panelsToPersist, activeTabId, accountId } = args;
  const container = document.getElementById(containerId);
  if (!container) {
    console.warn("GridStack container not found to read positions");
    return [];
  }

  // Crea un set con los IDs que queremos persistir para que el querySelector sea más rápido de validar
  const idsToPersist = new Set(
    (panelsToPersist || [])
      .map(p => (p?.id != null ? String(p.id) : null))
      .filter(Boolean)
  );

  const widgets = container.querySelectorAll(".grid-stack-item");
  const result = [];

  widgets.forEach((el) => {
    const gsId = el.getAttribute("gs-id");
    if (!gsId || !idsToPersist.has(gsId)) return; // si quieres TODOS, quita este filtro

    const gsX = parseInt(el.getAttribute("gs-x") || "0", 10);
    const gsY = parseInt(el.getAttribute("gs-y") || "0", 10);
    const gsW = parseInt(el.getAttribute("gs-w") || el.getAttribute("gs-width") || "1", 10);
    const gsH = parseInt(el.getAttribute("gs-h") || el.getAttribute("gs-height") || "1", 10);

    // Busca el panel original en memoria para obtener panelId si lo necesitas
    const found = panelsToPersist.find(p => String(p.id) === gsId) || {};
    console.log(found, "info Found for gsId", gsId);
    const panelId = found.id;

    result.push({
      id: Number(found.tableId),          // id del item en tu grid (mapping/row), ajusta si tu backend espera otro id
      xPos: gsX,
      yPos: gsY,
      width: gsW,
      height: gsH,
      panel: { id: Number(panelId) },
      tab: { id: Number(activeTabId) },
      accountId: String(accountId),
    });
  });

  return result;
}

/**
 * Envía el payload a tu servicio `update-dashboard-panel-position`.
 * Ajusta la URL y auth según tu backend.
 *
 * @param {Array<Object>} payload
 */
async function sendPositionsToBackend(payload) {
  // Evita enviar vacío
  if (!Array.isArray(payload) || payload.length === 0) return;

  updatePanelDashboardPosition(payload);

  
}

  // Función local que replica getContentByPanel del servicio
  const getContentByPanel = async (panel) => {
    //TODO ask if there is any panel type
    if (!panel.panelType) {
      return JSON.stringify({
        name: "ConfigurePanel",
        props: {},
      });
    }

    if (panel.panelType === "Pie" && panel.datasourceId) {
      try {
        const configResponse = await getPieConfigurationByPanelId(panel.id);
        const dataResponse = await getConsumeDataByPanelId(panel.id);

        const data = dataResponse.data.map((row) => {
          const obj = {};
          dataResponse.columns.forEach((column, index) => {
            obj[column] = row[index];
          });
          return obj;
        });

        return JSON.stringify({
          name: "PieChart",
          props: {
            metadata: configResponse.piePanelDef,
            data,
            showLegend: configResponse.piePanelDef.showLegend || false,
            legendPosition: configResponse.piePanelDef.legendPosition,
            showNumeric: configResponse.piePanelDef.showNumeric,
            showPercentage: configResponse.piePanelDef.showPercentage,
            innerRadius: configResponse.piePanelDef.innerRadius || 0,
            thresholdValues: configResponse.thresholdValues || [],
          },
        });
      } catch (error) {
        console.error(`Error loading pie data for panel ${panel.id}:`, error);
        return JSON.stringify({
          name: "PieChart",
          props: {
            data: [],
            showLegend: true,
          },
        });
      }
    }

    if (panel.panelType === "Table" && panel.datasourceId) {
      try {
        const configResponse = await getTableConfigurationByPanelId(panel.id);
        const dataResponse = await getConsumeDataByPanelId(panel.id);

        const metadata = configResponse.tableColumns.map((column) => ({
          referencedColumnName: column.referencedColumnName,
          label: column.label,
          specificWidth: column.specificWidth,
          horizontalAlignment: column.horizontalAlignment,
          show: true,
          useNumberFormat: column.useNumberFormat,
          prefix: column.prefix,
          suffix: column.suffix,
          decimalNumbers: column.decimalNumbers,
        }));

        const data = dataResponse.data.map((cells) => {
          const initialValueAcc = {};
          const row = cells.reduce((acc, value, key) => {
            acc[dataResponse.columns[key]] = value;
            return acc;
          }, initialValueAcc);
          return row;
        });

        return JSON.stringify({
          name: "TableComponent",
          props: {
            metadata,
            data,
            alternatingRowColor: configResponse.tableConfig.alternatingRowColor,
            fitColumns: configResponse.tableConfig.fitColumns,
            thresholdValues: configResponse.thresholdValues || [],
          },
        });
      } catch (error) {
        console.error(`Error loading table data for panel ${panel.id}:`, error);
        return JSON.stringify({
          name: "TableComponent",
          props: {},
        });
      }
    }

    if (panel.panelType === "XY Series" && panel.datasourceId) {
      try {
        const configResponse = await getXyChartConfigurationByPanelId(panel.id);
        const dataResponse = await getConsumeDataByPanelId(panel.id);
        return JSON.stringify({
          name: "XyChart",
          props: {
            configuration: {
              ...configResponse,
              data: dataResponse.data,
              columns: dataResponse.columns,
            },
            isPreview: false,
          },
        });
      } catch (error) {
        console.error(`Error loading xy data for panel ${panel.id}:`, error);
        return JSON.stringify({
          name: "XyChart",
          props: {
            configuration: {
              data: [],
              columns: [],
            },
            isPreview: false,
          },
        });
      }
    }

    if (panel.panelType === "KPI" && panel.datasourceId) {
      try {
        const configResponse = await getKpiConfigurationByPanelId(panel.id);
        const dataResponse = await getConsumeDataByPanelId(panel.id);

        const data = dataResponse.data.map((row) => {
          const obj = {};
          dataResponse.columns.forEach((column, index) => {
            obj[column] = row[index];
          });
          return obj;
        });

        // Transform comparison data if it exists
        let auxComparisonData = null;
        if (dataResponse.auxComparation && dataResponse.auxComparation.data) {
          auxComparisonData = dataResponse.auxComparation.data.map((row) => {
            const obj = {};
            dataResponse.auxComparation.columns.forEach((column, index) => {
              obj[column] = row[index];
            });
            return obj;
          });
        }

        return JSON.stringify({
          name: "KpiChart",
          props: {
            data,
            metadata: configResponse,
            auxComparisonData,
          },
        });
      } catch (error) {
        console.error(`Error loading KPI data for panel ${panel.id}:`, error);
        return JSON.stringify({
          name: "KpiChart",
          props: {
            value: 0,
            label: "KPI",
          },
        });
      }
    }
  };

  // Función optimizada que evita la llamada a getPanelsByTabId
  const createOptimizedPanelInTab = async (panelData, activeTabId) => {
    // Obtener restricciones por panelType
    let restrictionsArr = [];
    try {
      restrictionsArr = await fetchPanelSizeRestrictions();
    } catch (e) {
      restrictionsArr = [];
    }
    const restrictionsObj = {};
    restrictionsArr.forEach((r) => {
      restrictionsObj[r.panelType] = {
        minWidth: r.minWidth,
        maxWidth: r.maxWidth,
        minHeight: r.minHeight,
        maxHeight: r.maxHeight,
      };
    });
    const restriction = restrictionsObj[panelData.panelType] || {};

    // Aplicar restricciones a las dimensiones antes de enviar al servidor
    let requestedWidth = panelData.initialWidth || panelData.width || 50;
    let requestedHeight = panelData.initialHeight || panelData.height || 20;

    // Aplicar límites mínimos y máximos
    if (restriction.minWidth && requestedWidth < restriction.minWidth) {
      requestedWidth = restriction.minWidth;
    }
    if (restriction.maxWidth && requestedWidth > restriction.maxWidth) {
      requestedWidth = restriction.maxWidth;
    }
    if (restriction.minHeight && requestedHeight < restriction.minHeight) {
      requestedHeight = restriction.minHeight;
    }
    if (restriction.maxHeight && requestedHeight > restriction.maxHeight) {
      requestedHeight = restriction.maxHeight;
    }

    const body = {
      title: panelData.title || "New Panel",
      description: panelData.description || "creating new panel",
      panelType: panelData.panelType || null,
      height: requestedHeight,
      width: requestedWidth,
      xPos: panelData.xPos || 0,
      yPos: panelData.yPos !== undefined ? panelData.yPos : 0,
      tabId: activeTabId,
      accountId: localStorage.getItem("u_account_id"),
      panelId: panelData.panelId,
      minWidth: restriction.minWidth,
      maxWidth: restriction.maxWidth,
      minHeight: restriction.minHeight,
      maxHeight: restriction.maxHeight,
    };

    const response = await axios.post(environment.createPanelDashboard, body);

    // Asegurar que las dimensiones retornadas respeten las restricciones
    let finalWidth = response.data.load.width || requestedWidth;
    let finalHeight = response.data.load.height || requestedHeight;

    // Aplicar restricciones nuevamente a las dimensiones finales por seguridad
    if (restriction.minWidth && finalWidth < restriction.minWidth) {
      finalWidth = restriction.minWidth;
    }
    if (restriction.maxWidth && finalWidth > restriction.maxWidth) {
      finalWidth = restriction.maxWidth;
    }
    if (restriction.minHeight && finalHeight < restriction.minHeight) {
      finalHeight = restriction.minHeight;
    }
    if (restriction.maxHeight && finalHeight > restriction.maxHeight) {
      finalHeight = restriction.maxHeight;
    }

    return {
      x: response.data.load.xPos,
      y: response.data.load.yPos,
      w: finalWidth,
      h: finalHeight,
      id: response.data.load.panel.id,
      tableId: response.data.load.id,
      title: response.data.load.panel.title || body.title, // Incluir título del servidor
      description: response.data.load.panel.description || body.description, // Incluir descripción del servidor
      // Propiedades para almacenamiento/referencia
      minWidth: restriction.minWidth,
      maxWidth: restriction.maxWidth,
      minHeight: restriction.minHeight,
      maxHeight: restriction.maxHeight,
      // Propiedades específicas de GridStack para restricciones de redimensionamiento
      minW: restriction.minWidth,
      maxW: restriction.maxWidth,
      minH: restriction.minHeight,
      maxH: restriction.maxHeight,
      content: await getContentByPanel(response.data.load.panel),
    };
  };

  // Normalizar los paneles XY Series en predefinedPanels con cache y estado reactivo por panel
  const normalizePanels = useCallback(
    async (panels) => {
      return await Promise.all(
        panels.map(async (panel) => {
          // Si el panel no tiene datasourceId, no está configurado
          if (!panel.datasourceId) {
            setPanelLoadingState((prev) => ({ ...prev, [panel.id]: "error" }));
            return { ...panel, status: "error" };
          }
          // XY Series: async config/data solo si no está en cache y no está fetch en curso
          if (panel.panelType === "XY Series" && panel.datasourceId) {
            if (panelCacheRef.current[panel.id]) {
              setPanelLoadingState((prev) => ({
                ...prev,
                [panel.id]: "loaded",
              }));
              return { ...panelCacheRef.current[panel.id], status: "loaded" };
            }
            if (panelFetchStatusRef.current[panel.id]) {
              // Si ya hay un fetch en curso, devolver panel en loading
              return { ...panel, status: "loading" };
            }
            try {
              panelFetchStatusRef.current[panel.id] = true;
              setPanelLoadingState((prev) => ({
                ...prev,
                [panel.id]: "loading",
              }));
              const configResponse = await getXyChartConfigurationByPanelId(
                panel.id
              );
              const dataResponse = await getConsumeDataByPanelId(panel.id);
              const normalizedPanel = {
                ...panel,
                configuration: {
                  ...configResponse,
                  data: dataResponse.data,
                  columns: dataResponse.columns,
                },
                status: "loaded",
              };
              panelCacheRef.current[panel.id] = normalizedPanel;
              setPanelLoadingState((prev) => ({
                ...prev,
                [panel.id]: "loaded",
              }));
              panelFetchStatusRef.current[panel.id] = false;
              return normalizedPanel;
            } catch (error) {
              const errorPanel = {
                ...panel,
                configuration: {
                  data: [],
                  columns: [],
                },
                status: "error",
              };
              panelCacheRef.current[panel.id] = errorPanel;
              setPanelLoadingState((prev) => ({
                ...prev,
                [panel.id]: "error",
              }));
              panelFetchStatusRef.current[panel.id] = false;
              return errorPanel;
            }
          }
          // Otros paneles: si tienen configuración, están cargados
          if (panel.configuration) {
            setPanelLoadingState((prev) => ({ ...prev, [panel.id]: "loaded" }));
            return { ...panel, status: "loaded" };
          }
          // Si no tienen configuración pero tienen datasourceId, están cargando
          setPanelLoadingState((prev) => ({ ...prev, [panel.id]: "loading" }));
          return { ...panel, status: "loading" };
        })
      );
    },
    [setPanelLoadingState, panelCacheRef]
  );

  useEffect(() => {
    async function normalizeAndSet() {
      if (predefinedPanels && predefinedPanels.length > 0) {
        const result = await normalizePanels(predefinedPanels);
        setNormalizedPanels(result);
      } else {
        setNormalizedPanels([]);
      }
    }
    normalizeAndSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predefinedPanels, normalizePanels]);

  // Efecto para re-normalizar paneles que siguen en 'loading' pero ya tienen datos en el cache
  useEffect(() => {
    if (!normalizedPanels.length) return;
    const stillLoading = normalizedPanels.filter(
      (panel) => panel.status === "loading" && panelCacheRef.current[panel.id]
    );
    if (stillLoading.length) {
      const updatedPanels = normalizedPanels.map((panel) => {
        if (panel.status === "loading" && panelCacheRef.current[panel.id]) {
          return { ...panelCacheRef.current[panel.id], status: "loaded" };
        }
        return panel;
      });
      setNormalizedPanels(updatedPanels);
    }
  }, [panelLoadingState, normalizedPanels]);

  // Efecto para actualizar paneles en 'loading' cuando el cache cambia
  useEffect(() => {
    if (!normalizedPanels.length) return;
    let needsUpdate = false;
    const updatedPanels = normalizedPanels.map((panel) => {
      if (panel.status === "loading" && panelCacheRef.current[panel.id]) {
        needsUpdate = true;
        return { ...panelCacheRef.current[panel.id], status: "loaded" };
      }
      return panel;
    });
    if (needsUpdate) {
      setNormalizedPanels(updatedPanels);
    }
  }, [Object.values(panelCacheRef.current).length]);

  // Polling corto para paneles en 'loading' (máximo 2 segundos)
  useEffect(() => {
    if (!normalizedPanels.length) return;
    const interval = setInterval(() => {
      let needsUpdate = false;
      const updatedPanels = normalizedPanels.map((panel) => {
        if (panel.status === "loading" && panelCacheRef.current[panel.id]) {
          needsUpdate = true;
          return { ...panelCacheRef.current[panel.id], status: "loaded" };
        }
        return panel;
      });
      if (needsUpdate) {
        setNormalizedPanels(updatedPanels);
      }
    }, 300);
    // Limitar el polling a 2 segundos
    const timeout = setTimeout(() => clearInterval(interval), 2000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [normalizedPanels]);

  // Devuelve los panels normalizados en vez de los originales
  return {
    categories,
    subcategories,
    predefinedPanels: normalizedPanels,
    selectedPrefinedPanels,
    panelLoadingMap,
    workStream,
    activeTabId,
    onCategoryClick,
    onSubcategoryClick,
    onPanelClick,
    importPanelsToGridstack,
    panelLoadingState,
  };
};
